import { useCallback, useMemo } from 'react'

import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import { StyleSheet, TextStyle, ViewStyle } from 'react-native'

import { dsv } from '../styles/defaults'

export interface MultipleSelectListProps {
  /**
   * Fn to set Selected option value which will be stored in your local state
   */
  setSelected: (selected: string[]) => void

  /**
   * Placeholder text that will be displayed in the select box
   */
  placeholder?: string

  /**
   * Additional styles for select box
   */
  boxStyles?: ViewStyle

  /**
   *  	Additional styles for text of select box
   */
  inputStyles?: TextStyle

  /**
   *  	Additional styles for dropdown scrollview
   */
  dropdownStyles?: ViewStyle

  /**
   *  Additional styles for dropdown list item
   */
  dropdownItemStyles?: ViewStyle

  /**
   * Additional styles for list items text
   */
  dropdownTextStyles?: TextStyle

  /**
   * Maximum height of the dropdown wrapper to occupy
   */
  maxHeight?: number

  disabled?: boolean

  /**
   * Data which will be iterated as options of select list
   */
  readonly data: Array<L1Keys>

  currentSelections: Array<string>

  /**
   * The default option of the select list
   */
  defaultOption?: { key: string; value: string }

  /**
   * Kept for API compatibility. MultipleSelectList is intentionally selection-only.
   */
  searchicon?: JSX.Element

  /**
   * Kept for API compatibility. MultipleSelectList is intentionally selection-only.
   */
  arrowicon?: JSX.Element

  /**
   * Kept for API compatibility. MultipleSelectList is intentionally selection-only.
   */
  search?: boolean

  /**
   * Kept for API compatibility. MultipleSelectList is intentionally selection-only.
   */
  searchPlaceholder?: string

  /**
   * Trigger an action when option is selected
   */
  onSelect?: () => void

  /**
   * set text of label which appears soon after multiple values are selected
   */
  label?: string

  /**
   * set fontFamily of whole component Text
   */
  fontFamily?: string

  /**
   * set this to change the default search failure text
   */
  notFoundText?: string

  /**
   * Additional styles for disabled list item
   */
  disabledItemStyles?: ViewStyle

  /**
   * Additional styles for disabled list items text
   */
  disabledTextStyles?: TextStyle

  /**
   * Additional styles for disabled checkbox
   */
  disabledCheckBoxStyles?: ViewStyle

  /**
   * Additional styles for checkbox
   */
  checkBoxStyles?: ViewStyle

  /**
   * What to store inside your local state (key or value)
   */
  save?: 'key' | 'value'

  /**
   * Control the dropdown with this prop
   */
  dropdownShown?: boolean

  /**
   * Kept for API compatibility. MultipleSelectList is intentionally selection-only.
   */
  closeicon?: JSX.Element

  /**
   * Additional styles for multiselect badge
   */
  badgeStyles?: ViewStyle

  /**
   * Additional styles for multiselect badge text
   */
  badgeTextStyles?: TextStyle

  /**
   * Additional styles for label
   */
  labelStyles?: TextStyle
}

type L1Keys = { key?: string; value?: string; disabled?: boolean | undefined }

type MultiSelectOption = {
  key: string
  value: string
  disabled?: boolean
}

const MultipleSelectList = ({
  fontFamily,
  setSelected,
  placeholder,
  boxStyles,
  inputStyles,
  dropdownStyles,
  currentSelections,
  dropdownItemStyles,
  dropdownTextStyles,
  maxHeight = 350,
  data,
  onSelect = () => {},
  disabledItemStyles,
  disabledTextStyles,
  badgeStyles,
  badgeTextStyles,
  save = 'key',
  disabled,
}: MultipleSelectListProps) => {
  const flattenedBoxStyles = useMemo(() => StyleSheet.flatten(boxStyles) ?? {}, [boxStyles])
  const flattenedInputStyles = useMemo(() => StyleSheet.flatten(inputStyles) ?? {}, [inputStyles])
  const flattenedDropdownStyles = useMemo(() => StyleSheet.flatten(dropdownStyles) ?? {}, [dropdownStyles])
  const flattenedDropdownItemStyles = useMemo(
    () => StyleSheet.flatten(dropdownItemStyles) ?? {},
    [dropdownItemStyles]
  )
  const flattenedDropdownTextStyles = useMemo(
    () => StyleSheet.flatten(dropdownTextStyles) ?? {},
    [dropdownTextStyles]
  )
  const flattenedDisabledItemStyles = useMemo(() => StyleSheet.flatten(disabledItemStyles) ?? {}, [disabledItemStyles])
  const flattenedDisabledTextStyles = useMemo(() => StyleSheet.flatten(disabledTextStyles) ?? {}, [disabledTextStyles])
  const flattenedBadgeStyles = useMemo(() => StyleSheet.flatten(badgeStyles) ?? {}, [badgeStyles])
  const flattenedBadgeTextStyles = useMemo(() => StyleSheet.flatten(badgeTextStyles) ?? {}, [badgeTextStyles])

  const options = useMemo<MultiSelectOption[]>(
    () => data.map((item) => ({
      key: String(item.key ?? item.value ?? ''),
      value: String(item.value ?? item.key ?? ''),
      disabled: item.disabled,
    })),
    [data]
  )

  const optionLabelBySelectionValue = useMemo(() => {
    return new Map(
      options.map((option) => [save === 'value' ? option.value : option.key, option.value])
    )
  }, [options, save])

  const selectedValues = useMemo(() => currentSelections.map(String), [currentSelections])

  const updateSelection = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value
      const nextValues = typeof value === 'string' ? value.split(',') : value
      setSelected(nextValues)
      onSelect()
    },
    [onSelect, setSelected]
  )

  return (
    <Select
      disabled={disabled}
      displayEmpty
      multiple
      MenuProps={{
        PaperProps: {
          sx: {
            ...flattenedDropdownStyles,
            maxHeight,
            border: `1px solid ${dsv.colors.connectGrau30}`,
            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.16)',
          },
        },
      }}
      onChange={updateSelection}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return placeholder ?? 'Optionen auswählen'
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {selected.map((selection) => (
              <Chip
                key={selection}
                label={optionLabelBySelectionValue.get(selection) ?? selection}
                size="small"
                sx={{
                  ...flattenedBadgeStyles,
                  maxWidth: 220,
                  backgroundColor: dsv.colors.connectGrau70,
                  color: dsv.colors.white,
                  fontFamily: fontFamily ?? flattenedBadgeTextStyles.fontFamily ?? dsv.text.fontFamily,
                  fontSize: flattenedBadgeTextStyles.fontSize ?? 12,
                  '.MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            ))}
          </div>
        )
      }}
      sx={{
        width: '100%',
        minHeight: flattenedBoxStyles.minHeight ?? 38,
        backgroundColor: flattenedBoxStyles.backgroundColor ?? dsv.colors.base,
        borderRadius: flattenedBoxStyles.borderRadius ?? 1,
        color: flattenedInputStyles.color ?? dsv.colors.neutral,
        fontFamily: fontFamily ?? flattenedInputStyles.fontFamily ?? dsv.text.fontFamily,
        fontSize: flattenedInputStyles.fontSize ?? 14,
        ...flattenedBoxStyles,
        '.MuiSelect-select': {
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          minHeight: 'auto',
          padding: '6px 32px 6px 12px',
          color: selectedValues.length === 0 ? dsv.colors.connectGrau50 : undefined,
          ...flattenedInputStyles,
          fontFamily: fontFamily ?? flattenedInputStyles.fontFamily ?? dsv.text.fontFamily,
          fontSize: flattenedInputStyles.fontSize ?? 14,
        },
        '.MuiOutlinedInput-notchedOutline': {
          borderColor: flattenedBoxStyles.borderColor ?? dsv.colors.connectGrau30,
        },
      }}
      value={selectedValues}
    >
      {options.length === 0 ? (
        <MenuItem disabled value="__empty" sx={{ fontSize: 14 }}>
          Keine Optionen vorhanden
        </MenuItem>
      ) : options.map((option) => {
        const selectionValue = save === 'value' ? option.value : option.key
        const isSelected = selectedValues.includes(selectionValue)
        return (
          <MenuItem
            disabled={option.disabled === true}
            key={option.key}
            value={selectionValue}
            sx={{
              ...flattenedDropdownItemStyles,
              ...flattenedDropdownTextStyles,
              fontSize: flattenedDropdownTextStyles.fontSize ?? 14,
              minHeight: 34,
              '&.Mui-disabled': {
                ...flattenedDisabledItemStyles,
                ...flattenedDisabledTextStyles,
                opacity: 0.7,
              },
            }}
          >
            <Checkbox checked={isSelected} size="small" />
            <ListItemText
              primary={option.value}
              primaryTypographyProps={{
                fontFamily: fontFamily ?? flattenedDropdownTextStyles.fontFamily ?? dsv.text.fontFamily,
                fontSize: flattenedDropdownTextStyles.fontSize ?? 14,
              }}
            />
          </MenuItem>
        )
      })}
    </Select>
  )
}

export default MultipleSelectList
